import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ThemeProvider } from 'styled-components'

import { Header, Rows } from './components'
import { GENERIC_DATA_GRID_ID, useGridColumnWidths, useGridId } from './DataGrid.hooks'
import * as S from './DataGrid.styles'
import { DEFAULT_THEME } from './DataGrid.theme'
import { ColumnConfig, DataGridProps, ResizedColumnsParams } from './DataGrid.types'
import { deleteContext, generateContext } from './ExtensionsContext'
import { genericMemo, gridClassNames, StickyController } from './utils'

function _DataGrid<RowType>({
  children,
  className,
  columnGroupHeaderRenderer,
  columns,
  data,
  extensions = [],
  gridId: gridIdProp = GENERIC_DATA_GRID_ID,
  gridTheme: gridThemeProp,
  groupRowsByField,
  isResizable,
  onCellClick,
  onHeaderCellClick,
  pinnedColumns = 0,
  pinnedColumnsRight = 0,
  rowsGroupHeaderCellRenderer,
  rowsGroupHeaderRowRenderer,
  selectedRowIndexes,
  selectedColumnIndexes,
  skipFromGroupingField,
  virtualization,
}: DataGridProps<RowType>) {
  const [currentColumns, setCurrentColumns] = useState<ColumnConfig<RowType>[]>(columns)
  const gridId = useGridId(gridIdProp)
  const { columnGridWidths, updateColumnGridWidths } = useGridColumnWidths(columns, gridId)

  const gridTheme = useMemo(
    () => ({
      ...DEFAULT_THEME,
      ...gridThemeProp,
      header: { ...DEFAULT_THEME.header, ...gridThemeProp?.header },
    }),
    [gridThemeProp],
  )

  const stickyControllerRef = useRef(new StickyController(gridId, gridTheme, columns.length))

  const context = generateContext(gridId, extensions)

  const handleOnColumnResize = ({ columnKey, updatedColumnWidth }: ResizedColumnsParams) => {
    const updatedColumns = currentColumns.map(column => {
      if (column.columnKey === columnKey) {
        return {
          ...column,
          width: `${updatedColumnWidth}px`,
        }
      }

      return column
    })

    setCurrentColumns(updatedColumns)
    updateColumnGridWidths(updatedColumns)
  }

  useEffect(() => {
    return () => {
      deleteContext(gridId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    stickyControllerRef.current?.updateColumnsGroupingSetup(columns)

    if (currentColumns) {
      setCurrentColumns(columns)
      updateColumnGridWidths(columns)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns])

  const ExtensionsProvider = context.Provider

  return (
    <ExtensionsProvider value={extensions}>
      <ThemeProvider theme={gridTheme}>
        <S.ScrollWrapper className={gridClassNames.scrollWrapper}>
          <S.Root id={gridId} className={className} $gridColTemplate={columnGridWidths}>
            <Header
              columns={currentColumns}
              columnGroupHeaderRenderer={columnGroupHeaderRenderer}
              gridId={gridId}
              isResizable={isResizable}
              key={gridId}
              onColumnResize={handleOnColumnResize}
              pinnedColumns={pinnedColumns}
              pinnedColumnsRight={pinnedColumnsRight}
              stickyControllerRef={stickyControllerRef}
              selectedColumnIndexes={selectedColumnIndexes}
              onHeaderCellClick={onHeaderCellClick}
            />
            <Rows
              columns={currentColumns}
              data={data}
              gridId={gridId}
              stickyControllerRef={stickyControllerRef}
              pinnedColumns={pinnedColumns}
              pinnedColumnsRight={pinnedColumnsRight}
              groundByRenderer={rowsGroupHeaderRowRenderer}
              groupByCellRenderer={rowsGroupHeaderCellRenderer}
              groupByField={groupRowsByField}
              skipFromGroupingField={skipFromGroupingField}
              onCellClick={onCellClick}
              selectedRowIndexes={selectedRowIndexes}
              selectedColumnIndexes={selectedColumnIndexes}
              virtualization={virtualization}
            />
          </S.Root>
          {children}
        </S.ScrollWrapper>
      </ThemeProvider>
    </ExtensionsProvider>
  )
}

export const DataGrid = genericMemo(_DataGrid)
// Inside some child component
